import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, BrowserAuthError, EndSessionPopupRequest, EndSessionRequest, PublicClientApplication } from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { AsmAuthStorageService } from '@spartacus/asm/root';
import {
  AuthMultisiteIsolationService,
  AuthRedirectService,
  AuthService,
  GlobalMessageService,
  GlobalMessageType,
  OAuthLibWrapperService,
  RoutingService,
  StateWithClientAuth,
  UserIdService,
} from '@spartacus/core';
import { msalConfig } from '../../../SGRE-config/auth-config';
import { StorageService } from '../storage.service';
import { environment } from '../../../../environments/environment';
import { WindowRef } from '@spartacus/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Version of AuthService that is working for both user na CS agent.
 * Overrides AuthService when ASM module is enabled.
 */
@Injectable({
  providedIn: 'root',
})
export class MSALSpaAuthService extends AuthService {

  public static msalInstance = new PublicClientApplication(msalConfig);
  public isIframe: boolean;
  public static channel = new BroadcastChannel("SGRE_SAP_CS_Channel");
  private loginInProgress = false;
  private initializationPromise: Promise<void>;
  
  // Store redirect response to ensure it's not missed by component subscriptions
  private redirectResponse$ = new BehaviorSubject<AuthenticationResult | null>(null);

  constructor(
    private windowRef: WindowRef,
    protected storageService: StorageService,
    protected msalService: MsalService,
    protected override store: Store<StateWithClientAuth>,
    protected override userIdService: UserIdService,
    protected override oAuthLibWrapperService: OAuthLibWrapperService,
    protected override authStorageService: AsmAuthStorageService,
    protected override authRedirectService: AuthRedirectService,
    protected globalMessageService: GlobalMessageService,
    protected override routingService: RoutingService,
    protected override authMultisiteIsolationService?: AuthMultisiteIsolationService,
  ) {
    super(
      store,
      userIdService,
      oAuthLibWrapperService,
      authStorageService,
      authRedirectService,
      routingService,
      authMultisiteIsolationService
    );
    this.isIframe =
      (this.windowRef.nativeWindow !== this.windowRef.nativeWindow?.parent) &&
      !this.windowRef.nativeWindow?.opener;
    
    // Initialize MSAL instance and handle redirects synchronously
    this.initializationPromise = this.initializeMSAL();
  }

  private async initializeMSAL(): Promise<void> {
    try {
      await this.msalService.instance.initialize();
      
      // Handle any pending redirect
      const response = await this.msalService.instance.handleRedirectPromise();
      if (response) {
        // Store the response so components can process it even if they subscribe late
        this.redirectResponse$.next(response);
      }
    } catch (error) {
      console.error('[MSAL] Error during initialization:', error);
    }
  }

  getAndClearRedirectResponse(): AuthenticationResult | null {
    const response = this.redirectResponse$.value;
    if (response) {
      this.redirectResponse$.next(null);
    }
    return response;
  }

  get redirectResponse(): Observable<AuthenticationResult | null> {
    return this.redirectResponse$.asObservable();
  }

  async safeLogin(usePopup: boolean = false): Promise<void> {
    try {
      // CRITICAL: Wait for MSAL to be fully initialized
      await this.initializationPromise;

      // Check if user is already authenticated
      const accounts = this.msalService.instance.getAllAccounts();
      
      if (accounts.length > 0) {
        // CRITICAL: User is authenticated with Azure AD but might not have backend session
        // Acquire token silently - this will trigger MSAL broadcast events that the component listens to
        try {
          const tokenRequest = {
            scopes: ['openid', 'profile', 'email', 'User.Read'],
            account: accounts[0]
          };
          
          // Use msalService (not instance) to trigger broadcast events
          await this.msalService.acquireTokenSilent(tokenRequest);
          
        } catch (silentError: any) {
          console.warn('[MSAL] Silent token acquisition failed:', silentError);
          console.warn('[MSAL] Error code:', silentError?.errorCode);
          
          // If silent acquisition fails due to interaction required, try interactive login
          if (silentError?.errorCode === 'consent_required' || 
              silentError?.errorCode === 'interaction_required' ||
              silentError?.errorCode === 'login_required') {
            // Don't return, let it fall through to the interactive login below
          } else {
            console.error('[MSAL] Unexpected silent token error:', silentError);
            throw silentError;
          }
        }
        
        return;
      }

      // Prevent duplicate login calls
      if (this.loginInProgress) {
        console.warn('[MSAL] Login already in progress, aborting duplicate call');
        return;
      }

      this.loginInProgress = true;

      // Prepare login request
      const loginRequest = {
        scopes: ['openid', 'profile', 'email', 'User.Read']
      };

      // Initiate login based on environment
      if (usePopup) {
        await this.msalService.loginPopup(loginRequest);
      } else {
        await this.msalService.loginRedirect(loginRequest);
        // Note: Code after this won't execute due to redirect
      }
    } catch (error: any) {
      console.error('[MSAL] === LOGIN ERROR ===');
      console.error('[MSAL] Error type:', error?.constructor?.name);
      console.error('[MSAL] Error code:', error?.errorCode);
      console.error('[MSAL] Error message:', error?.errorMessage || error?.message);
      console.error('[MSAL] Full error:', error);
      
      // Handle the specific interaction_in_progress error gracefully
      if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
        console.warn('[MSAL] Interaction already in progress - this is expected if multiple buttons were clicked');
        return;
      }
      
      // Show user-friendly error message
      this.globalMessageService.add(
        { key: 'loginForm.loginError' },
        GlobalMessageType.MSG_TYPE_ERROR
      );
      throw error;
    } finally {
      // Reset flag after delay to allow redirect to complete
      timer(2000).pipe(
        tap(() => {
          this.loginInProgress = false;
        })
      ).subscribe();
    }
  }
  override coreLogout(_showLogoutConfirmationMessage = false): Promise<void> {
    return super.coreLogout().finally(() => {
      this.MSALlogout();
    })
  }

  MSALlogout() {
    MSALSpaAuthService.channel.postMessage("TRIGGER_LOGOUT");
    const currentAccount = this.msalService.instance.getAllAccounts()?.at(0);

    let session: EndSessionRequest | EndSessionPopupRequest = {
      account: currentAccount,
      postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
    };
    console.log('currentAccount:', currentAccount);// Will be removed
    console.log('postLogout redirectURL:', msalConfig.auth.postLogoutRedirectUri);// Will be removed

    if (this.isIframe) { 
      (session as any)['mainWindowRedirectUri'] = environment.msalConfigDetails.redirectUrls.smartEdit; 
    }

    this.isIframe
      ? this.msalService.logoutPopup(session)
      : this.msalService.logoutRedirect(session);

    this.msalService.instance.clearCache();
    this.storageService.reset();
    this.storageService.resetSessionDetails();
  }
}